// client/src/components/history/EventHistoryArticle.jsx
//
// Tenant editor for one event's public "article":
//   * a public/hidden toggle + editable intro paragraph (seeded from the
//     event's own data — description + spelled-out date)
//   * each media file with its own public/hidden toggle and an editable
//     caption (seeded from the social-post caption it was posted with), shown
//     in chronological order of being made public.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaHome, FaSave, FaEye, FaEyeSlash, FaExternalLinkAlt, FaTh, FaList } from "react-icons/fa";
import AppFooter from "../AppFooter";
import MediaViewer from "../media/MediaViewer";
import {
  getEventArticle, saveEventArticle, saveFileCaption, toggleFilePublicVisibility,
} from "../../services/historyApi";

const isVideoMime = (m) => /^video\//i.test(m || "");

export default function EventHistoryArticle() {
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [event, setEvent] = useState(null);
  const [generatedIntro, setGeneratedIntro] = useState("");

  const [introText, setIntroText] = useState("");
  const [introPublic, setIntroPublic] = useState(false);
  const [introDirty, setIntroDirty] = useState(false);

  // media: [{ fileId, url, mimetype, kind, itemName, createdOn, modifiedOn,
  //           showInPublicHistory, caption, defaultCaption }]
  const [media, setMedia] = useState([]);
  const [captions, setCaptions] = useState({});        // fileId -> current text
  const [savedCaptions, setSavedCaptions] = useState({}); // fileId -> persisted text
  const [togglingId, setTogglingId] = useState(null);

  const [mediaViewer, setMediaViewer] = useState(null); // { url, isVideo }
  const [mediaViewMode, setMediaViewMode] = useState(() => {
    try { return localStorage.getItem("os_historyarticle_media_view") || "single"; } catch { return "single"; }
  });
  const setMediaViewModeAndPersist = (mode) => {
    setMediaViewMode(mode);
    try { localStorage.setItem("os_historyarticle_media_view", mode); } catch {}
  };

  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.classList.add("eventlist-fullbleed");
    return () => document.body.classList.remove("eventlist-fullbleed");
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await getEventArticle(tenant, eventId);
      setEvent(data.event);
      setGeneratedIntro(data.generatedIntro || "");
      const serverIntro = data.event?.intro == null ? "" : data.event.intro;
      setIntroText(serverIntro || data.generatedIntro || "");
      setIntroPublic(!!data.event?.introPublic);
      setIntroDirty(false);

      const list = Array.isArray(data.media) ? data.media : [];
      setMedia(list);
      const cap = {};
      const savedCap = {};
      list.forEach((m) => {
        // Show the saved caption, or the default as a starting point. Seed the
        // "saved" baseline to the same text so a blur without an edit is a
        // no-op — we only POST once the tenant actually changes something.
        cap[m.fileId] = m.caption || m.defaultCaption || "";
        savedCap[m.fileId] = cap[m.fileId];
      });
      setCaptions(cap);
      setSavedCaptions(savedCap);
    } catch (e) {
      setErr(e?.message || "Failed to load event article");
    } finally {
      setLoading(false);
    }
  }, [tenant, eventId]);

  useEffect(() => { load(); }, [load]);

  // Public media in chronological order of being made public (modifiedOn asc),
  // then the not-yet-public media by upload order.
  const ordered = useMemo(() => {
    const ts = (v) => new Date(v || 0).getTime() || 0;
    const pub = media.filter((m) => m.showInPublicHistory).sort((a, b) => ts(a.modifiedOn) - ts(b.modifiedOn));
    const hid = media.filter((m) => !m.showInPublicHistory).sort((a, b) => ts(a.createdOn) - ts(b.createdOn));
    return { pub, hid };
  }, [media]);

  const handleToggleIntroPublic = () => { setIntroPublic((v) => !v); setIntroDirty(true); };

  const handleIntroChange = (v) => { setIntroText(v); setIntroDirty(true); };

  const resetIntroToDefault = () => { setIntroText(generatedIntro); setIntroDirty(true); };

  const persistIntro = async () => {
    // Save null when the text is empty or still exactly the generated default,
    // so the article keeps tracking the event's data until the tenant customises.
    const trimmed = (introText || "").trim();
    const intro = (!trimmed || trimmed === (generatedIntro || "").trim()) ? null : introText;
    const res = await saveEventArticle(tenant, eventId, { intro, introPublic });
    setIntroPublic(!!res.introPublic);
    setIntroDirty(false);
  };

  const persistCaption = async (fileId) => {
    const text = captions[fileId] || "";
    if (text === (savedCaptions[fileId] || "")) return;
    await saveFileCaption(tenant, fileId, text);
    setSavedCaptions((prev) => ({ ...prev, [fileId]: text }));
    setMedia((prev) => prev.map((m) => (m.fileId === fileId ? { ...m, caption: text } : m)));
  };

  const handleToggleMedia = async (m) => {
    const next = !m.showInPublicHistory;
    setTogglingId(m.fileId);
    try {
      await toggleFilePublicVisibility(tenant, m.fileId, next);
      // Going public stamps modifiedon server-side; mirror that locally so the
      // card re-sorts to the end of the chronological list.
      setMedia((prev) => prev.map((x) => (
        x.fileId === m.fileId
          ? { ...x, showInPublicHistory: next, modifiedOn: new Date().toISOString() }
          : x
      )));
    } catch (e) {
      setStatus("Could not update visibility. Please try again.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setStatus("");
    try {
      if (introDirty) await persistIntro();
      const dirtyFiles = Object.keys(captions).filter(
        (id) => (captions[id] || "") !== (savedCaptions[id] || "")
      );
      for (const id of dirtyFiles) {
        // eslint-disable-next-line no-await-in-loop
        await saveFileCaption(tenant, Number(id), captions[id] || "");
      }
      setSavedCaptions((prev) => {
        const next = { ...prev };
        dirtyFiles.forEach((id) => { next[id] = captions[id] || ""; });
        return next;
      });
      setStatus("Saved.");
    } catch (e) {
      setStatus(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (m) => (
    <article
      key={`media-${m.fileId}`}
      className="feed-card"
      style={{ padding: 0, border: "1px solid rgba(0,0,0,0.15)", borderRadius: 12, overflow: "hidden" }}
    >
      <div
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        onClick={() => setMediaViewer({ url: m.url, isVideo: isVideoMime(m.mimetype) })}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setMediaViewer({ url: m.url, isVideo: isVideoMime(m.mimetype) })}
      >
        {isVideoMime(m.mimetype) ? (
          <video className="feed-media" src={m.url} preload="metadata" playsInline muted />
        ) : (
          <img className="feed-media" src={m.url} alt={m.itemName || "event media"} loading="lazy" />
        )}
      </div>

      <div style={{ padding: "10px 12px", background: "#253866" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
          <span style={{ color: "#a8a8a8", fontSize: 12 }}>
            {m.kind === "item" ? (m.itemName || "Event item") : "Event"}
          </span>
          <button
            type="button"
            className={`btn btn-sm ${m.showInPublicHistory ? "btn-outline-success" : "btn-outline-secondary"}`}
            onClick={() => handleToggleMedia(m)}
            disabled={togglingId === m.fileId}
            aria-label={`${m.showInPublicHistory ? "Hide" : "Show"} media ${m.fileId}`}
            title={m.showInPublicHistory ? "Visible in public article" : "Hidden from public article"}
          >
            {m.showInPublicHistory ? <FaEye className="mr-1" /> : <FaEyeSlash className="mr-1" />}
            {m.showInPublicHistory ? "Public" : "Hidden"}
          </button>
        </div>
        <textarea
          className="form-control"
          rows={2}
          value={captions[m.fileId] ?? ""}
          placeholder={m.defaultCaption || "Add a caption…"}
          onChange={(e) => setCaptions((prev) => ({ ...prev, [m.fileId]: e.target.value }))}
          onBlur={() => persistCaption(m.fileId).catch(() => setStatus("Caption save failed."))}
          style={{ whiteSpace: "pre-wrap" }}
        />
      </div>
    </article>
  );

  if (loading) {
    return <div className="container"><p style={{ padding: 16, color: "#a8a8a8" }}>Loading…</p></div>;
  }
  if (err) {
    return (
      <div className="container">
        <div className="alert alert-danger" style={{ margin: 16 }}>{err}</div>
        <AppFooter buttons={[{ icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(-1) }]} />
      </div>
    );
  }

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">{event?.name || "Event"}</h2>

      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${tenant}/eventHistory`)}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/dashboard`)}>
          <FaHome /> Home
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/${eventId}/publicEventArticle`)}>
          <FaExternalLinkAlt /> View public article
        </button>
        <button type="button" className="btn-primary-action" onClick={handleSaveAll} disabled={saving}>
          <FaSave /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {status && <p className="status-message" style={{ maxWidth: 640, margin: "8px auto" }}>{status}</p>}

      {/* Intro block */}
      <section style={{ maxWidth: 640, margin: "0 auto 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
          <label className="font-semibold" htmlFor="article-intro">Intro text</label>
          <button
            type="button"
            className={`btn btn-sm ${introPublic ? "btn-outline-success" : "btn-outline-secondary"}`}
            onClick={handleToggleIntroPublic}
            aria-label="Toggle intro visibility"
            title={introPublic ? "Shown on the public article" : "Hidden from the public article"}
          >
            {introPublic ? <FaEye className="mr-1" /> : <FaEyeSlash className="mr-1" />}
            {introPublic ? "Public" : "Hidden"}
          </button>
        </div>
        <textarea
          id="article-intro"
          className="form-control"
          rows={5}
          value={introText}
          placeholder={generatedIntro}
          onChange={(e) => handleIntroChange(e.target.value)}
          onBlur={() => introDirty && persistIntro().catch(() => setStatus("Intro save failed."))}
          style={{ whiteSpace: "pre-wrap" }}
        />
        <div style={{ marginTop: 4 }}>
          <button
            type="button"
            className="btn btn-link btn-sm"
            style={{ padding: 0 }}
            onClick={resetIntroToDefault}
          >
            Reset to default
          </button>
        </div>
      </section>

      {/* Media */}
      <div className="media-column-header">
        <h3 style={{ margin: 0 }}>Media</h3>
        <div className="media-view-toggle" role="group" aria-label="Media view mode">
          <button
            type="button"
            className={`view-toggle-btn ${mediaViewMode === "single" ? "active" : ""}`}
            onClick={() => setMediaViewModeAndPersist("single")}
            aria-label="Single column view"
          >
            <FaList />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${mediaViewMode === "grid" ? "active" : ""}`}
            onClick={() => setMediaViewModeAndPersist("grid")}
            aria-label="Grid view"
          >
            <FaTh />
          </button>
        </div>
      </div>

      {media.length === 0 && <p style={{ padding: "0 12px", color: "#a8a8a8" }}>No media on this event yet.</p>}

      {ordered.pub.length > 0 && (
        <div className={`item-media-cards item-media-cards--${mediaViewMode}`}>
          {ordered.pub.map(renderCard)}
        </div>
      )}

      {ordered.hid.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 12px 10px" }}>
            <hr style={{ flex: 1, borderColor: "rgba(255,255,255,0.15)" }} />
            <span style={{ color: "#a8a8a8", fontSize: 13 }}>Not yet public</span>
            <hr style={{ flex: 1, borderColor: "rgba(255,255,255,0.15)" }} />
          </div>
          <div className={`item-media-cards item-media-cards--${mediaViewMode}`}>
            {ordered.hid.map(renderCard)}
          </div>
        </>
      )}

      {mediaViewer && (
        <MediaViewer
          src={mediaViewer.url}
          isVideo={mediaViewer.isVideo}
          onClose={() => setMediaViewer(null)}
        />
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(`/${tenant}/eventHistory`) },
          { icon: <FaHome />, label: "Home", onClick: () => navigate(`/${tenant}/dashboard`) },
          { icon: <FaSave />, label: saving ? "Saving…" : "Save", onClick: handleSaveAll, disabled: saving },
        ]}
      />
    </div>
  );
}
